import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import type { Product } from '../../types'
import * as db from '../../services/db'
import { generateId } from '../../utils/uuid'

const CATEGORIES = ['Fruits', 'Vegetables', 'Dairy', 'Snacks', 'Drinks', 'Pantry']

export function ProductFormView() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isNew = !id
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [category, setCategory] = useState('Fruits')
  const [imagePath, setImagePath] = useState('🛒')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isNew && id) {
      db.getProductById(id).then((p) => {
        if (p) {
          setName(p.name)
          setPrice(String(p.price))
          setCategory(p.category)
          setImagePath(p.imagePath)
        }
      })
    }
  }, [id, isNew])

  const save = async () => {
    const num = parseFloat(price)
    if (!name.trim() || Number.isNaN(num) || num < 0) return
    setSaving(true)
    const product: Product = isNew
      ? {
          id: generateId(),
          name: name.trim(),
          imagePath: imagePath || '🛒',
          price: num,
          category,
          qrCodeValue: generateId(),
          isPrebuilt: false,
        }
      : {
          id: id!,
          name: name.trim(),
          imagePath: imagePath || '🛒',
          price: num,
          category,
          qrCodeValue: (await db.getProductById(id!))?.qrCodeValue ?? id!,
          isPrebuilt: (await db.getProductById(id!))?.isPrebuilt ?? false,
        }
    await db.addProduct(product)
    setSaving(false)
    navigate('/parent')
  }

  const emojiSuggestions = ['🛒', '🍎', '🥕', '🥛', '🍪', '🥤', '🍞', '🧀', '🥚', '🍅', '🥦', '🍇', '🫐', '🥑', '🍌']

  return (
    <>
      <h2 className="page-title">{isNew ? 'Add Product' : 'Edit Product'}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400, width: '100%' }}>
        <label>
          <span style={{ display: 'block', marginBottom: 4 }}>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Product name"
            style={{ width: '100%', padding: 12, fontSize: '1rem', borderRadius: 8, border: '2px solid #e0e0e0' }}
          />
        </label>
        <label>
          <span style={{ display: 'block', marginBottom: 4 }}>Price ($)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
            style={{ width: '100%', padding: 12, fontSize: '1rem', borderRadius: 8, border: '2px solid #e0e0e0' }}
          />
        </label>
        <label>
          <span style={{ display: 'block', marginBottom: 4 }}>Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={{ width: '100%', padding: 12, fontSize: '1rem', borderRadius: 8, border: '2px solid #e0e0e0' }}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label>
          <span style={{ display: 'block', marginBottom: 4 }}>Icon (emoji)</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {emojiSuggestions.map((e) => (
              <button
                key={e}
                type="button"
                className="btn-secondary"
                style={{ padding: 8, fontSize: '1.25rem' }}
                onClick={() => setImagePath(e)}
              >
                {e}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={imagePath}
            onChange={(e) => setImagePath(e.target.value)}
            placeholder="Emoji"
            style={{ width: 80, padding: 12, fontSize: '1.5rem', textAlign: 'center', borderRadius: 8, border: '2px solid #e0e0e0' }}
          />
        </label>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button type="button" className="btn-large btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to="/parent" className="btn-large btn-secondary" style={{ textDecoration: 'none' }}>
            Cancel
          </Link>
        </div>
      </div>
    </>
  )
}
